import React from 'react'

import { 
    Modal,
    Box,
    Button, 
    Typography,
    CircularProgress,
    IconButton
} from '@mui/material'

// MUI Icons
import CloseIcon from '@mui/icons-material/Close'


const DeleteTemplateConfirmationDialog = (props) => {
    const {
        onClose,
        onDelete,
        value: valueProp,
        open,
        loading = false,
        sectionName = "selected section",
        ...other
    } = props

    const [value, setValue] = React.useState(valueProp)
    const dialogRef = React.useRef(null)

    React.useEffect(() => {
        if (!open) {
            setValue(valueProp)
        }
    }, [open, valueProp])

    const handleEntering = () => {
        if (dialogRef.current !== null) {
            dialogRef.current.focus()
        }
    }

    const handleCancel = () => {
        if (!loading) {
            onClose()
        }
    }

    const handleDelete = () => {
        onDelete()
        // Note: onClose() is now called by the parent component after the async operation completes
    }

    return (
        <Modal
            open={open}
            onClose={handleCancel}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '548px',
                    minHeight: loading ? '280px' : '240px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                    padding: '24px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px'
                }}
            >
                {/* Main Content Container */}
                <Box
                    sx={{
                        flexGrow: 1,
                        mb: '112px', // Space for fixed buttons
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                    }}
                >
                    {/* Header */}
                    <Box 
                        sx={{ 
                            width: '100%',
                            maxHeight: '102px',
                            minHeight: '66px',
                            mb: '4px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Close Button */}
                        <Box
                            sx={{
                                width: '500px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start'
                            }}
                        >
                            <IconButton
                                onClick={handleCancel}
                                disabled={loading}
                                sx={{
                                    width: '16px',
                                    height: '16px',
                                    padding: 0,
                                    minWidth: '16px',
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: '#F3F4F6',
                                        borderRadius: '6px'
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.5
                                    }
                                }}
                            >
                                <CloseIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                            </IconButton>
                        </Box>

                        {/* Title */}
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                mt: '-4px'
                            }}
                        >
                            <Typography sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                fontSize: '1.5rem',
                                fontWeight: '700',
                                color: '#374151',
                                minHeight: '36px',
                                maxHeight: '72px'
                            }}>
                                Delete Section
                            </Typography>
                        </Box>
                    </Box>

                    {/* Section Name Display */}
                    <Box
                        sx={{
                            mb: '16px',
                            flexShrink: 0
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '12px',
                                color: '#6B7280',
                                fontWeight: 700,
                                lineHeight: '18px',
                                mb: '4px'
                            }}
                        >
                            Section to delete:
                        </Typography>
                        <Box
                            sx={{
                                width: '500px',
                                borderRadius: '6px',
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                padding: '10px 12px'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    color: '#374151',
                                    fontWeight: '500'
                                }}
                            >
                                {sectionName}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Loading Section */}
                    {loading && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            py: 2,
                            gap: 2
                        }}>
                            <CircularProgress 
                                size={24} 
                                sx={{ 
                                    color: '#1F477D'
                                }} 
                            />
                            <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                                Deleting section...
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Action Buttons */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '40px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '12px',
                        width: '375px'
                    }}
                >
                    <Button 
                        onClick={handleCancel} 
                        disabled={loading}
                        sx={{
                            width: '50%',
                            height: '48px',
                            borderRadius: '6px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            border: '1px solid #D1D5DB',
                            fontSize: '16px',
                            fontWeight: 500,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#F9FAFB'
                            },
                            '&.Mui-disabled': {
                                opacity: 0.6,
                                border: '1px solid #D1D5DB',
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDelete} 
                        disabled={loading}
                        sx={{
                            width: '50%',
                            height: '48px',
                            borderRadius: '6px',
                            backgroundColor: '#1F477D',
                            color: '#FFFFFF',
                            fontSize: '16px',
                            fontWeight: 500,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#163A63',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#1F477D',
                                opacity: 0.7,
                            }
                        }}
                    >
                        Yes, delete this section
                    </Button>
                </Box>
            </Box>
        </Modal>
    )
}

export default DeleteTemplateConfirmationDialog